import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { TextArea } from "@tql/ui-components-react/kendo/inputs";
import { Hint } from "@tql/ui-components-react/kendo/labels";
import {
	Card,
	CardBody,
	CardHeader,
} from "@tql/ui-components-react/kendo/layout";
import { infoCircle } from "@tql/ui-components-react/untitled/icons";
import { useId } from "react";

import { useStopDraft } from "../../hooks/use-shipment-draft.ts";
import { formatSpecialInstructions } from "../../utils/format-special-instructions.ts";
import { BolNotations } from "./bol-notations.tsx";
import * as styles from "./special-instructions.module.css";

const SPECIAL_INSTRUCTIONS_MAX_LENGTH = 500;

type SpecialInstructionsEditProps = {
	readonly pickupInstructions?: string;
	readonly deliveryInstructions?: string;
	readonly bolNotations?: string;
};

export function SpecialInstructionsEdit({
	pickupInstructions = "",
	deliveryInstructions = "",
	bolNotations,
}: SpecialInstructionsEditProps) {
	const { stop: pickup, updateStop: updatePickup } = useStopDraft("pickup");
	const characterCountId = useId();

	// Show the merged pickup + delivery text until a draft has been seeded, but
	// once the pickup draft has a value, edits only ever touch pickup - we don't
	// re-split changes back out to delivery on save, and address book
	// selections never modify this field.
	const pickupValue =
		pickup?.specialInstructions ??
		formatSpecialInstructions(pickupInstructions, deliveryInstructions);

	return (
		<div className={"card-wrapper"}>
			<Card>
				<CardHeader>
					<h3>Special Instructions</h3>
				</CardHeader>
				<CardBody>
					<div className={styles.content}>
						<BolNotations bolNotations={bolNotations} />
						<TextArea
							aria-label={"Pickup Special Instructions"}
							ariaDescribedBy={characterCountId}
							autoSize={true}
							className={styles.textarea}
							maxLength={SPECIAL_INSTRUCTIONS_MAX_LENGTH}
							onChange={(event) =>
								updatePickup({
									specialInstructions: String(event.value ?? ""),
								})
							}
							rows={3}
							value={pickupValue}
						/>
						<Hint className={styles.characterCount} id={characterCountId}>
							<span className={styles.characterCountLabel}>
								Total character count
							</span>
							<SvgIcon
								className={styles.infoIcon}
								icon={infoCircle}
								themeColor={"primary"}
							/>
							{pickupValue.length} / {SPECIAL_INSTRUCTIONS_MAX_LENGTH}
						</Hint>
					</div>
				</CardBody>
			</Card>
		</div>
	);
}
